import { apiAuthType, apiBaseUrl } from '@/config/env';
import {
  decryptResponsePayload,
  encryptRequestPayload,
  shouldApplyEncryptionBasedOnApiConfig,
} from '@/helpers/encryption.helpers';
import type { SystemConfig } from '@/interfaces/systemConfig.interface';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import {
  applyConcurrentRequestDedupe,
  attachConcurrentDedupeKey,
} from './base.api.adapter';
import { exchangeEncryptionKey } from './encryption.api';

let accessToken = '';
let systemConfig: SystemConfig | null = null;

/**
 * Extract a displayable error message from API error payload.
 * Message may be a string, or nested in detail.message, detail, message, or an array of validation errors.
 */
function toErrorMessage(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    const msg =
      first && typeof first === 'object' && 'msg' in first
        ? (first as { msg?: unknown }).msg
        : first && typeof first === 'object' && 'message' in first
          ? (first as { message?: unknown }).message
          : first;
    return toErrorMessage(msg);
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message.trim() || null;
    if (typeof obj.msg === 'string') return obj.msg.trim() || null;
  }
  return null;
}

/**
 * Get a user-facing error message from any thrown value (e.g. API or Axios errors).
 * Use this in catch blocks to show consistent backend error messages in toasts.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = 'An error occurred',
): string {
  if (error != null && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: Record<string, unknown> } })
      .response?.data;
    if (data) {
      const detail = data.detail;
      const msg =
        toErrorMessage(detail) ??
        toErrorMessage(data.message) ??
        (error instanceof Error ? error.message : null);
      if (msg) return msg;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function setSystemConfig(config: SystemConfig) {
  systemConfig = config;
}

export function setAccessToken(token: string) {
  accessToken = token;
  api.defaults.headers.Authorization = token ? `Bearer ${token}` : '';
}

/**
 * Check if a request should skip encryption
 * This includes streaming requests and encryption API endpoints (to avoid circular dependencies)
 */
function shouldSkipEncryption(config: AxiosRequestConfig): boolean {
  const url = config.url || '';

  if (
    !systemConfig?.ENCRYPTOR || // Skip encryption if the encryptor is not set
    url.includes('/encryption/') || // Skip encryption for encryption API endpoints (to avoid circular dependencies)
    config.headers?.['X-Connection-Type'] === 'stream' // Skip encryption for streaming requests
  ) {
    return true;
  }

  return false;
}

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
    'x-auth-type': apiAuthType,
  },
});

applyConcurrentRequestDedupe(api);

// Cap how long any data-query HTTP call may run before axios aborts it.
// Long-running Neo4j queries do exist on this app, so the cap is generous
// (90s) — the goal is to convert a SW-induced hang (request intercepted but
// never resolved or rejected) into a clean rejection, NOT to bound legitimate
// query latency. Scoped to `/query` so unrelated endpoints (uploads, exports,
// system config) keep their default no-timeout behavior.
const QUERY_REQUEST_TIMEOUT_MS = 90_000;

api.interceptors.request.use(async (config) => {
  config.baseURL = apiBaseUrl;
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Bound query requests to surface SW hangs as a clean axios timeout error
  // instead of an indefinite pending promise. Only applied if the caller
  // hasn't already supplied a timeout for this specific request.
  if (
    !config.timeout &&
    typeof config.url === 'string' &&
    config.url.includes('/query')
  ) {
    config.timeout = QUERY_REQUEST_TIMEOUT_MS;
  }

  // Same logical request in flight (e.g. Strict Mode) → one HTTP call; key uses plain body (pre-encryption).
  attachConcurrentDedupeKey(config);

  // Skip encryption for streaming requests and encryption endpoints
  if (shouldSkipEncryption(config)) {
    return config;
  }

  // Check if encryption should be applied based on API configuration
  const url = config.url || '';
  const method = config.method?.toUpperCase() || 'GET';
  const shouldEncrypt = shouldApplyEncryptionBasedOnApiConfig(
    method,
    url,
    'request',
  );

  // Only encrypt JSON request bodies if encryption is enabled for this request
  if (
    shouldEncrypt &&
    config.data &&
    typeof config.data === 'object' &&
    !(config.data instanceof FormData) &&
    !(config.data instanceof Blob) &&
    systemConfig?.ENCRYPTOR
  ) {
    const encryptionResult = await encryptRequestPayload(
      config.data,
      systemConfig.ENCRYPTOR,
    );
    if (encryptionResult) {
      config.data = encryptionResult.encryptedData;
      config.headers = config.headers || {};
      Object.assign(config.headers, encryptionResult.headers);
    }
  }

  return config;
});

api.interceptors.response.use(
  async (response: AxiosResponse) => {
    // Skip decryption for streaming responses and encryption endpoints
    const config = response.config;
    if (shouldSkipEncryption(config)) {
      return response;
    }

    // Check if decryption should be applied based on API configuration
    const url = config.url || '';
    const method = config.method?.toUpperCase() || 'GET';
    const shouldDecrypt = shouldApplyEncryptionBasedOnApiConfig(
      method,
      url,
      'response',
    );

    // Check if response has encryption exchange key header
    const exchangeEncryptionKeyValue =
      response.headers['x-decrypt-exchange-key'] ||
      response.headers['X-Decrypt-Exchange-Key'];

    if (
      shouldDecrypt &&
      exchangeEncryptionKeyValue &&
      systemConfig?.ENCRYPTOR
    ) {
      // Exchange the key for decryption key.
      //
      // Bound by an explicit axios `timeout` because this call goes through
      // the OIDC service worker on the same request path that just succeeded
      // — and the SW has been observed to silently intercept follow-up
      // requests during token-renew races without ever resolving them
      // (separate from the documented "Failed to fetch" throw path that
      // axios surfaces as `Network Error`). When that happens the parent
      // /query promise hangs indefinitely because we're awaiting the
      // exchange before completing the response, leaving dashboard cards
      // spinning forever on cold-cache loads.
      //
      // Critical: the timeout MUST be passed into axios (not raced via
      // `Promise.race` around the awaiter), because only an axios-level
      // timeout actually rejects the underlying transport promise. That
      // rejection is what triggers `base.api.adapter.ts`'s `.finally()`
      // cleanup that removes the dedupe-map entry — without it, the stuck
      // request stays in `inflightByDedupeKey` forever and every subsequent
      // identical exchange call attaches to the same hung promise.
      const EXCHANGE_TIMEOUT_MS = 10_000;
      const responseEncryptionKey = await exchangeEncryptionKey(
        exchangeEncryptionKeyValue,
        { timeout: EXCHANGE_TIMEOUT_MS },
      );

      response.data = await decryptResponsePayload(
        response.data,
        responseEncryptionKey.data.key,
        systemConfig.ENCRYPTOR,
      );
    }

    return response;
  },
  (error) => {
    // Preserve the original error object so we can access response details in specific handlers
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;

      // For HTTP errors, preserve the response data (message can appear in any of these)
      const data = error.response?.data as Record<string, unknown> | undefined;
      const detail = data?.detail;
      let errorMessage =
        toErrorMessage(detail) ??
        toErrorMessage(data?.message) ??
        toErrorMessage(error.message) ??
        'An error occurred';

      // Provide more accurate error messages for authentication/authorization errors
      switch (status) {
        case 401:
          errorMessage =
            'Unauthorized: Please check your authentication credentials.';
          break;
        case 403:
          errorMessage =
            'Forbidden: You do not have permission to access this resource.';
          break;
        default:
          if (status >= 500) {
            errorMessage =
              errorMessage || 'Server error: Please try again later.';
          }
          break;
      }

      const enhancedError = new Error(errorMessage);
      (enhancedError as any).response = error.response;
      (enhancedError as any).status = status;
      (enhancedError as any).statusText = statusText;
      throw enhancedError;
    }
    // For network errors, just throw the original error
    throw error;
  },
);

export default api;
